import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  OnDestroy,
  ViewChildren,
  QueryList,
  HostListener,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import * as _ from 'lodash';

import { Segment } from '../segment.interface';
import { SegmentsNavigationService } from './segments-navigation.service';
import { TabButtonDirective } from 'shared/button/tab-button.directive';


@Component({
  moduleId: module.id,
  selector: 'segments-navigation',
  templateUrl: 'segments-navigation.component.html',
  styleUrls: ['segments-navigation.component.scss'],
  providers: [SegmentsNavigationService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SegmentsNavigationComponent implements OnChanges, OnDestroy {
  @Input() segments: Array<Segment> = [];
  @Input() btnWidth: number = 158;
  @Input() sideNavSize: number = 150;
  @Input() marginLeft: number = 145;
  @Input() rightPaginationWidth: number = 120;
  @Input() showControls: boolean = true;
  @Input() hoverTop: boolean = false;
  @Input() size: string; // ['big', ]
  // For defining fixed content ['auto', '100%', Number]
  @Input() containerWidth: any;

  @Output() selected = new EventEmitter<any>();

  @ViewChildren(TabButtonDirective) tabButtons: QueryList<TabButtonDirective>;

  @HostListener('window:resize', ['$event']) onResize() {
    this.ref.markForCheck();
  }

  constructor(
    public segmentsNavService: SegmentsNavigationService,
    private ref: ChangeDetectorRef,
  ) {}

  get isNextDisabled(): boolean {
    return this.segmentsNavService.isNextDisabled;
  }

  get isPrevDisabled(): boolean {
    return this.segmentsNavService.isPrevDisabled;
  }

  get panelContainerWidth(): string {
    if (this.containerWidth) {
      if (isNaN(this.containerWidth)) { return this.containerWidth; }
      return `${this.containerWidth}px`;
    } else {
      return `${this.segmentsNavService.containerWidth}px`;
    }
  }

  get showSegmentControls() {
    // need to show controls for scroll because of screen width
    const needToScroll = this.segmentsNavService.scrollingArea < this.segmentsNavService.containerWidth;
    return needToScroll && this.showControls;
  }

  get model(): any {
    return this.segmentsNavService.model;
  }

  ngOnChanges() {
    this.setSegmentsNames();
    this.segmentsNavService.init({
      segments: this.segments,
      btnWidth: this.btnWidth,
      sideNavSize: this.sideNavSize,
      marginLeft: this.marginLeft,
      rightPaginationWidth: this.rightPaginationWidth
    });
    this.ref.markForCheck();
  }

  ngOnDestroy() {
    this.segmentsNavService.reset();
  }

  onPaginationBtnClick(direction: number) {
    this.segmentsNavService.changeNavPosition(direction);
    this.ref.markForCheck();
  }

  onNavButtonClick(segment: Segment) {
    if (segment.disabled) { return; }
    this.setActive(segment.name);
    this.selected.emit(segment);
  }

  public setActive(name: string): void {
    this.segmentsNavService.setActive(name);
    this.ref.markForCheck();
    if (!this.showControls) { return; }
    const position = this.btnWidth * this.model.activeIndex + 1;
    if (position > this.segmentsNavService.scrollingArea || position <= Math.abs(this.model.navPosition)) {
      this.segmentsNavService.scrollToIndex(this.model.activeIndex);
    }
  }

  public getActive(): Segment {
    return this.segments.find((segment: Segment) => segment.active);
  }

  public toggleCircle(name: string, toggle?: boolean) {
    this.segments.some((segment: Segment) => {
      if (segment.name === name) {
        segment.circleIcon = !!toggle;
        return true;
      }
      return false;
    });
    this.ref.markForCheck();
  }

  public toggleCircles(func: Function): void {
    _.each(this.model.segments, (segment: any) => {
      segment.circleIcon = func.call(this, segment);
    });
    this.ref.markForCheck();
  }

  private segmentName(title: string): string {
    return title.replace(/\s/g, '-').toLowerCase();
  }

  private setSegmentsNames(): void {
    this.segments.forEach((segment: Segment) => {
      if (!segment.name) { segment.name = this.segmentName(segment.title); }
    });
  }

}
